/**
 * The table figure rule (FS-4): the unit lives in the column header, never in the cell; whole numbers by default; two decimals only where a convention says so
 * (a per-share price, a margin or loss or capital rate that moves in basis points). One helper prints a cell from its unit, so no table chooses its own decimals.
 * The KPI-level formatting of a named measure stays in `../format` (`formatKpi`): this is for a column of figures in a table.
 */
export type FigureUnit = "usd" | "usdM" | "usdPerShare" | "pct" | "pct2" | "bp" | "pts" | "count" | "x";

/** `header`: what the column header says after the label ("Loans ($M)"); `places`: the decimals of the cell. */
export const FIGURES: Readonly<Record<FigureUnit, { readonly header: string; readonly places: number }>> = {
  usd: { header: "$", places: 0 },
  usdM: { header: "$M", places: 0 },
  usdPerShare: { header: "$ a share", places: 2 },
  pct: { header: "%", places: 1 },
  pct2: { header: "%", places: 2 },
  bp: { header: "bp", places: 0 },
  pts: { header: "points", places: 1 },
  count: { header: "", places: 0 },
  x: { header: "×", places: 1 },
};

const MINUS = "−";

/** A figure without its unit: "1,234", "−3.6", "+12" (with `signed`); "—" for a figure that does not exist yet, never 0. */
export function figure(value: number | null | undefined, unit: FigureUnit, opts: { readonly signed?: boolean } = {}): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return "—";
  const places = FIGURES[unit].places;
  const text = Math.abs(value).toLocaleString("en-US", { minimumFractionDigits: places, maximumFractionDigits: places });
  const zero = Number(text.replace(/,/g, "")) === 0;
  if (zero) return text;
  return value < 0 ? `${MINUS}${text}` : opts.signed === true ? `+${text}` : text;
}

/** A column header's label with its unit, once: "Loans ($M)", "Margin (%)"; a count has none. */
export const headerWithUnit = (label: string, unit: FigureUnit | undefined): string => {
  const u = unit === undefined ? "" : FIGURES[unit].header;
  return u === "" ? label : `${label} (${u})`;
};
